import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import type { TurboSubmitEndEvent, TurboSubmitStartEvent } from "@hotwired/turbo"
import axios from "axios"
import ListController from "../clients/list_controller"
import { EnhancedSelect } from "../../utils/enhanced_select"

interface GroupOption {
  id: number
  title: string
  disabled: boolean
}

interface AvailableGroupResponse {
  id: number
  title: string
  already_member: boolean
}

export default class AddSelectedController extends Controller {
  declare addSelectedFormTarget: HTMLFormElement
  declare groupSelectTarget: HTMLSelectElement
  declare submitButtonTarget: HTMLButtonElement
  declare hasClientsListOutlet: boolean
  declare clientsListOutlet: ListController
  private singleClientIds: string[] | null = null
  private enhancedSelect: EnhancedSelect | null = null

  static targets = [
    "addSelectedForm",
    "groupSelect",
    "submitButton",
  ]
  static outlets = ["clients--list"]
  static values = {
    availableGroupsUrl: String,
  }
  declare availableGroupsUrlValue: string
  declare hasAvailableGroupsUrlValue: boolean

  disconnect() {
    this.destroyEnhancedSelect()
  }

  private initEnhancedSelect() {
    if (!this.hasAvailableGroupsUrlValue) return

    this.enhancedSelect = new EnhancedSelect(this.groupSelectTarget, {
      placeholder: "Search groups...",
      valueField: "id",
      labelField: "title",
      disableSearchFiltering: true, // Search is server-side
      disableQueryCache: true, // Force fresh server fetch on every query
      load: (query, signal) => this.searchGroups(query, signal),
      preload: true,
      onChange: () => this.updateSubmitState(),
      render: {
        option: (data, escape) => {
          const option = data as unknown as GroupOption
          const title = escape(option.title)
          if (option.disabled) {
            return `<div><span>${title}</span> <span class="badge text-bg-secondary ms-1">Already a member</span></div>`
          }
          return `<div>${title}</div>`
        },
      },
    })
  }

  private destroyEnhancedSelect() {
    if (this.enhancedSelect) {
      this.enhancedSelect.destroy()
      this.enhancedSelect = null
    }
  }

  private async searchGroups(query: string, signal: AbortSignal): Promise<GroupOption[]> {
    const clientIds = this.getClientIds()
    const params = new URLSearchParams()

    clientIds.forEach(id => params.append("client_ids[]", id))
    if (query) {
      params.append("q", query)
    }

    const response = await axios.get<AvailableGroupResponse[]>(
      `${this.availableGroupsUrlValue}?${params.toString()}`,
      { signal }
    )
    // Map `already_member` to TomSelect's `disabled` so members can't be selected.
    return response.data.map(({ id, title, already_member }) => ({
      id,
      title,
      disabled: already_member,
    }))
  }

  handleShow(event: Modal.Event) {
    const button = event.relatedTarget

    if (button && button.dataset.ids) {
      const ids = JSON.parse(button.dataset.ids)
      this.singleClientIds = ids.map(String)
    } else {
      this.singleClientIds = null
    }

    this.initEnhancedSelect()
  }

  private updateSubmitState(): void {
    const groupIds = this.getGroupIds()
    this.submitButtonTarget.disabled = groupIds.length === 0 || groupIds.every(id => id === "")
  }

  private getClientIds(): string[] {
    if (this.singleClientIds && this.singleClientIds.length > 0) {
      return this.singleClientIds
    }
    if (this.hasClientsListOutlet) {
      const selectionToolbarController = this.clientsListOutlet.getSelectionToolbarController()
      return selectionToolbarController?.selectedIds() || []
    }
    return []
  }

  private getGroupIds(): string[] {
    if (!this.enhancedSelect) return []
    const value = this.enhancedSelect.getValue()
    return Array.isArray(value) ? value : [value]
  }

  handleSubmitStart(event: TurboSubmitStartEvent) {
    const clientIds = this.getClientIds()
    const groupIds = this.getGroupIds()

    // Append client IDs
    clientIds.forEach(id => {
      event.detail.formSubmission.fetchRequest.body.append("client_ids[]", id)
    })

    // Append group IDs
    groupIds.forEach(id => {
      event.detail.formSubmission.fetchRequest.body.append("group_ids[]", id)
    })
  }

  handleSubmitEnd(event: TurboSubmitEndEvent) {
    if (event.detail.success) {
      // Close modal - will trigger handleHidden which clears selections
      const modal = globalThis.bootstrap.Modal.getInstance(this.element)
      modal?.hide()
    }
  }

  handleHidden() {
    this.singleClientIds = null

    if (this.hasClientsListOutlet) {
      const selectionToolbarController = this.clientsListOutlet.getSelectionToolbarController()
      selectionToolbarController?.clearSelected()
    }

    this.destroyEnhancedSelect()
  }
}
